import { motion } from "framer-motion";
import { Phone, Mail, ArrowUpRight, Clock } from "lucide-react";
import { BUSINESS, BOOKING_URL } from "@/config";

const Contact = () => {
  return (
    <section
      id="contact"
      className="relative py-20 md:py-32 bg-[#0B132B] overflow-hidden"
      data-testid="contact-section"
    >
      {/* decorative copper bar */}
      <div
        className="absolute right-0 top-24 w-1 h-32 bg-[#D47A4A] hidden lg:block"
        aria-hidden
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          {/* Left: heading + direct contact */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D47A4A] mb-4">
              / 06 — Get In Touch
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
              GET
              <br />
              <span className="text-[#D47A4A]">IN TOUCH.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#A0ABC0] max-w-md">
              Book a callout online in under a minute, or reach Conan directly
              by phone or email.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center gap-4 group"
                data-testid="contact-direct-phone"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#D47A4A]/10 border border-[#D47A4A]/30 rounded-sm group-hover:bg-[#D47A4A] transition-colors">
                  <Phone className="w-5 h-5 text-[#D47A4A] group-hover:text-[#0B132B] transition-colors" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#A0ABC0]">
                    Or call us directly
                  </p>
                  <p className="font-display text-xl md:text-2xl text-white tracking-wide">
                    {BUSINESS.phone}
                  </p>
                </div>
              </a>
              <a
                href={BUSINESS.emailHref}
                className="flex items-center gap-4 group"
                data-testid="contact-direct-email"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#D47A4A]/10 border border-[#D47A4A]/30 rounded-sm group-hover:bg-[#D47A4A] transition-colors">
                  <Mail className="w-5 h-5 text-[#D47A4A] group-hover:text-[#0B132B] transition-colors" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#A0ABC0]">
                    Email
                  </p>
                  <p className="text-base md:text-lg text-white break-all">
                    {BUSINESS.email}
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Right: Book Online CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 relative bg-[#151D33] border border-[#2C374D] rounded-sm p-8 md:p-12 overflow-hidden"
            data-testid="booking-cta-card"
          >
            {/* corner accents */}
            <div className="absolute top-0 left-0 w-12 h-px bg-[#D47A4A]" aria-hidden />
            <div className="absolute top-0 left-0 w-px h-12 bg-[#D47A4A]" aria-hidden />
            <div className="absolute bottom-0 right-0 w-12 h-px bg-[#D47A4A]" aria-hidden />
            <div className="absolute bottom-0 right-0 w-px h-12 bg-[#D47A4A]" aria-hidden />

            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D47A4A] mb-6">
                <span className="w-1.5 h-1.5 bg-[#D47A4A] rounded-full animate-pulse" />
                Online Booking
              </div>

              <h3 className="font-display text-3xl md:text-5xl text-white leading-[0.95] tracking-tight">
                BOOK A JOB
                <br />
                <span className="text-[#D47A4A]">IN UNDER A MINUTE.</span>
              </h3>

              <p className="mt-6 text-base md:text-lg text-[#A0ABC0] max-w-lg">
                Tell us what you need and pick a time that suits you. Submit
                your request directly through our secure booking system and we'll
                confirm the appointment as quickly as possible.
              </p>

              <ul className="mt-7 space-y-3 text-sm text-[#A0ABC0]">
                <li className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#D47A4A] flex-shrink-0" />
                  Fast, confirmed response — no chasing
                </li>
              </ul>

              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-cta-button"
                className="group mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#D47A4A] hover:bg-[#B85D2A] text-[#0B132B] font-bold uppercase tracking-[0.18em] px-9 py-5 rounded-md transition-all text-base md:text-lg shadow-lg shadow-[#D47A4A]/25 hover:shadow-[#D47A4A]/50 hover:-translate-y-0.5"
              >
                <Mail className="w-5 h-5" />
                Enquire Now
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>

              <p className="mt-5 text-xs uppercase tracking-[0.22em] text-[#A0ABC0]">
                Opens our secure booking system in a new tab
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
